import * as React from 'react';

import Button from 'components/atoms/v2/Button';
import RadioButton from 'components/atoms/v2/RadioButton';
import RadioGroup from 'components/atoms/v2/RadioGroup';
import LoadingIndicator from 'components/atoms/LoadingIndicator';
import { ShoppingCartIcon } from 'components/icons';
import { fetchProductOptions } from 'libs/apis/services/ProductOptionsService';
import type { GetProductOptionsResponse } from 'libs/apis/generated/models/GetProductOptionsResponse';
import type { ProductOption as ProductOptionResponse } from 'libs/apis/generated/models/ProductOption';
import type { OptionUnit as OptionUnitResponse } from 'libs/apis/generated/models/OptionUnit';
import classes from './SkuSelectModal.scss';

type StockStatus = 'in-stock' | 'sold-out' | 'discontinued';

type SkuUnit = {
  id: number;
  name: string;
  additionalPrice: number;
  price: number;
  sizePoint?: number;
  stockStatus: StockStatus;
  imageUrl?: string;
};

type SkuOption = {
  id: number;
  name: string;
  canReceiverSelectOption: boolean;
  units: SkuUnit[];
};

type SkuModalData = {
  options: SkuOption[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  csrfToken: string;
};

const MODAL_TITLE = 'カートに追加';
const FETCH_ERROR_MESSAGE = '商品情報の取得に失敗しました。時間をおいて再度お試しください。';

const resolveStockStatus = (unit: OptionUnitResponse): StockStatus => {
  if (unit.stock > 0) {
    return 'in-stock';
  }
  if (unit.isDiscontinued) {
    return 'discontinued';
  }
  return 'sold-out';
};

const toSkuUnit = (unit: OptionUnitResponse): SkuUnit => ({
  id: unit.id,
  name: unit.name,
  additionalPrice: unit.priceDiff,
  price: unit.price,
  sizePoint: unit.sizePoint,
  stockStatus: resolveStockStatus(unit),
  imageUrl: unit.imageUrl,
});

const toSkuOption = (option: ProductOptionResponse): SkuOption => ({
  id: option.id,
  name: option.name,
  canReceiverSelectOption: option.canReceiverSelectOption,
  units: option.units.map(toSkuUnit),
});

const toSkuModalData = (response: GetProductOptionsResponse): SkuModalData => ({
  options: response.options.map(toSkuOption),
});

const formatAdditionalPrice = (additionalPrice: number): string => {
  if (additionalPrice === 0) {
    return '';
  }
  return `（追加料金：￥${additionalPrice}）`;
};

type SkuUnitRowProps = {
  unit: SkuUnit;
  isDefault: boolean;
};

const SkuUnitRow: React.FC<SkuUnitRowProps> = React.memo(({ unit, isDefault }) => {
  const additionalPriceLabel = formatAdditionalPrice(unit.additionalPrice);

  const renderUnitInfo = () => (
    <div className={classes['skuSelectModal__unitInfo']}>
      {unit.imageUrl ? (
        <div className={classes['skuSelectModal__unitImageWrapper']}>
          <img
            className={classes['skuSelectModal__unitImage']}
            src={unit.imageUrl}
            alt={unit.name}
          />
        </div>
      ) : null}
      <div className={classes['skuSelectModal__unitTexts']}>
        <span className={classes['skuSelectModal__unitName']}>{unit.name}</span>
        {additionalPriceLabel ? (
          <span className={classes['skuSelectModal__unitAdditionalPrice']}>{additionalPriceLabel}</span>
        ) : null}
      </div>
    </div>
  );

  if (unit.stockStatus !== 'in-stock') {
    const isSoldOut = unit.stockStatus === 'sold-out';
    const statusLabel = isSoldOut ? '売り切れ' : '取扱終了';
    const statusClassName = isSoldOut
      ? classes['skuSelectModal__statusLabel--soldOut']
      : classes['skuSelectModal__statusLabel--discontinued'];

    return (
      <div className={classes['skuSelectModal__unitRow']}>
        {renderUnitInfo()}
        <p className={`${classes['skuSelectModal__statusLabel']} ${statusClassName}`}>{statusLabel}</p>
      </div>
    );
  }

  return (
    <RadioButton
      className={classes['skuSelectModal__unitRadio']}
      inputClassName="js-addCartSelect"
      value={unit.id}
      defaultChecked={isDefault}
    >
      {renderUnitInfo()}
    </RadioButton>
  );
});

SkuUnitRow.displayName = 'SkuUnitRow';

type SkuOptionBlockProps = {
  option: SkuOption;
  optionIndex: number;
  hasReceiverSelectableOption: boolean;
};

const SkuOptionBlock: React.FC<SkuOptionBlockProps> = React.memo(
  ({ option, optionIndex, hasReceiverSelectableOption }) => {
    const defaultUnitIndex = option.units.findIndex((unit) => unit.stockStatus === 'in-stock');
    const radioGroupName = `data[${optionIndex}][unit_ids][]`;

    return (
      <div className={'js-addCartOption'}>
        <input
          type="hidden"
          name={`data[${optionIndex}][option_id]`}
          value={option.id}
        />
        <p className={classes['skuSelectModal__optionHeader']}>「{option.name}」を選択</p>
        {hasReceiverSelectableOption && !option.canReceiverSelectOption ? (
          <p className={classes['skuSelectModal__optionNotice']}>※ こちらは受け取る方が変更できません。</p>
        ) : null}
        <RadioGroup
          name={radioGroupName}
          className={classes['skuSelectModal__radioGroup']}
        >
          <div className={classes['skuSelectModal__unitList']}>
            {option.units.map((unit, unitIndex) => (
              <SkuUnitRow
                key={unit.id}
                unit={unit}
                isDefault={unitIndex === defaultUnitIndex}
              />
            ))}
          </div>
        </RadioGroup>
      </div>
    );
  }
);

SkuOptionBlock.displayName = 'SkuOptionBlock';

const SkuSelectModal: React.FC<Props> = React.memo(({ isOpen, onClose, productId, csrfToken }) => {
  const [modalData, setModalData] = React.useState<SkuModalData | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let isCancelled = false;
    setIsLoading(true);
    setFetchError(null);
    setModalData(null);

    (async () => {
      const result = await fetchProductOptions({ productId });
      if (isCancelled) {
        return;
      }

      if (result.isSuccess()) {
        setModalData(toSkuModalData(result.value));
      } else {
        setFetchError(result.error.message ?? FETCH_ERROR_MESSAGE);
      }
      setIsLoading(false);
    })();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, productId]);

  if (!isOpen) {
    return null;
  }

  const hasReceiverSelectableOption = modalData
    ? modalData.options.some((option) => option.canReceiverSelectOption)
    : false;
  const unselectableOptionNames = modalData
    ? modalData.options.filter((option) => !option.canReceiverSelectOption).map((option) => option.name)
    : [];

  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const bodyContent = modalData ? (
    // prettier-ignore
    <form action="/carts/add" method="post" className={`${classes['skuSelectModal__form']} js-addCartModalForm`}>
      {csrfToken ? (<input type="hidden" name="_csrfToken" value={csrfToken} />) : null}
      <input type="hidden" name="prefer_egift" value="1" />
      <input type="hidden" name="product_id" value={productId} />
      {hasReceiverSelectableOption ? (
        <>
          <p className={classes['skuSelectModal__infoText']}>eギフトなら受け取る方が変更できるので気軽に選んでOK</p>
          <p className={classes['skuSelectModal__infoCaution']}>※ 在庫状況により変更できない場合があります。</p>
          <p className={classes['skuSelectModal__infoCaution']}>※ 購入手続き画面にて、お届け方法として「eギフト」を選択し、商品の公開設定を「公開」とした場合に限ります。</p>
          {unselectableOptionNames.length > 0 ? (
            <p className={classes['skuSelectModal__infoNote']}>
              ※
              {unselectableOptionNames.map((name) => (
                <span key={name}>「{name}」</span>
              ))}
              は変更できません。
            </p>
          ) : null}
        </>
      ) : null}
      {modalData.options.map((option, optionIndex) => (
        <SkuOptionBlock
          key={option.id}
          option={option}
          optionIndex={optionIndex}
          hasReceiverSelectableOption={hasReceiverSelectableOption}
        />
      ))}
      <div className={classes['skuSelectModal__footer']}>
        <Button
          type="submit"
          size="lg"
          fullWidth
          icon={<ShoppingCartIcon />}
          label="カートに追加"
          className={`${classes['skuSelectModal__submitButton']} gtm-cart-btn`}
        />
        <Button
          type="button"
          variant="secondary"
          fullWidth={false}
          label="閉じる"
          className={classes['skuSelectModal__footerCloseButton']}
          onClick={onClose}
        />
      </div>
    </form>
  ) : (
    <div className={classes['skuSelectModal__placeholder']}>
      {isLoading ? <LoadingIndicator size="lg" /> : fetchError ?? FETCH_ERROR_MESSAGE}
    </div>
  );

  return (
    <div
      className={classes['skuSelectModalOverlay']}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={classes['skuSelectModal']}
        role="dialog"
        aria-modal="true"
        aria-label={MODAL_TITLE}
        onClick={handleContainerClick}
      >
        <div className={classes['skuSelectModal__header']}>
          <p className={classes['skuSelectModal__title']}>{MODAL_TITLE}</p>
          <button
            type="button"
            className={classes['skuSelectModal__closeButton']}
            aria-label="閉じる"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className={classes['skuSelectModal__body']}>{bodyContent}</div>
      </div>
    </div>
  );
});

SkuSelectModal.displayName = 'SkuSelectModal';

export default SkuSelectModal;
